import React from 'react';

import {V2RequestsFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import * as NavConstants from 'constants/NavConstants';
import {Section, SECTIONS} from 'constants/NavConstants';
import {CrumbLink, SplitBreadcrumbs} from 'nav/Breadcrumbs';
import {ColumnAccessorKey} from 'nav/expanded/ListingRendererConstants';
import ItemListing from 'nav/sidepanel/listing/ItemListing';
import {getNavigationURL} from 'nav/sidepanel/listing/SearchURLs';
import {getSelectedPageSize} from 'nav/sidepanel/PageSizeUtil';
import RecentlyUsedRequestV2DefinitionCardView from 'processes/requests_v2/dashboard/RecentlyUsedRequestV2DefinitionsCardView';
import {SavedSearch} from 'search/saved-searches/SavedSearchUtil';
import {FilterKey, keyToEmptyFilter, SearchParams, SearchType, SortKey} from 'search/SearchConstants';
import {getAppliedSortsFromUserPreference} from 'search/SearchPreferences';
import * as SearchUtil from 'search/SearchUtil';
import withSearching, {NavigationUpdate, SearchProps} from 'search/withSearching';

import CreateRequestV2SubmissionButton from './CreateRequestV2SubmissionButton';

export function getDefaultRequestV2DefinitionListingSearchParams(): SearchParams {
  const userSorts = getAppliedSortsFromUserPreference(NavConstants.Section.REQUESTS_V2_DEFINITIONS);
  return {
    filters: [{...keyToEmptyFilter(FilterKey.TYPES), value: [ModelTypeConstants.REQUEST_V2_DEFINITION]}],
    limit: getSelectedPageSize(),
    offset: 0,
    query: '',
    sorts: userSorts.length > 0 ? userSorts : [{key: SortKey.SORT_NAME_KEY, reverse: false}],
  };
}

interface SidepanelProps {
  activeItemId: string | null;
  isCompactDisplay: boolean;
  setIsCompactDisplay: (isCompact: boolean) => void;
  isExpanded: boolean;
  requestsV2Tab: JSX.Element;
}

type RequestV2DefinitionListingProps = SidepanelProps & SearchProps;

export function RequestV2DefinitionListing(props: RequestV2DefinitionListingProps): JSX.Element {
  return (
    <div
      aria-labelledby={`${NavConstants.Section.REQUESTS_V2_DEFINITIONS}_TAB`}
      className="sidepanel"
      id={`${NavConstants.Section.REQUESTS_V2_DEFINITIONS}_PANEL`}
      role="tabpanel"
    >
      <ItemListing
        {...props}
        actionSource={ActionOrigin.REQUEST_V2_SIDEPANEL}
        additionalHeaderButtons={
          isFeatureEnabled({feature: V2RequestsFeature.V2_REQUESTS_CREATE}) ? (
            <CreateRequestV2SubmissionButton isExpanded={props.isExpanded} />
          ) : null
        }
        availableSortKeys={[SortKey.SORT_NAME_KEY]}
        // eslint-disable-next-line react/no-unstable-nested-components -- Disabling for now, but worth fixing
        makeCrumbs={(settingsComponents?: JSX.Element) => (
          <RequestV2DefinitionListingCrumbs
            activeSavedSearch={props.activeSavedSearch}
            onSelectSavedSearch={props.onSelectSavedSearch}
            onSearch={props.onSearch}
            searchParams={props.searchParams}
            settingsComponents={settingsComponents}
          />
        )}
        availableTypes={[ModelTypeConstants.REQUEST_V2_DEFINITION]}
        extraPrimaryFilterOptions={[]}
        preservedFilterKeys={[]}
        renderToolbar={() => null}
        section={Section.REQUESTS_V2_DEFINITIONS}
        sectionTabs={props.requestsV2Tab}
        sectionHeader={<RecentlyUsedRequestV2DefinitionCardView />}
        defaultHiddenColumnKeys={[
          ColumnAccessorKey.COLLECTION,
          ColumnAccessorKey.CREATOR,
          ColumnAccessorKey.CREATED_AT,
          ColumnAccessorKey.MODIFIED_AT,
          ColumnAccessorKey.PUBLISHING_STATUS,
        ]}
      />
    </div>
  );
}

function RequestV2DefinitionListingCrumbs(props: {
  activeSavedSearch?: SavedSearch;
  onSelectSavedSearch: (savedSearch?: SavedSearch) => Promise<void>;
  onSearch: (searchParams: SearchParams) => void;
  searchParams: SearchParams;
  settingsComponents?: JSX.Element;
}): JSX.Element {
  if (props.activeSavedSearch) {
    return (
      <SplitBreadcrumbs
        crumbs={[
          <CrumbLink
            key="requests-v2-types"
            text="Requests"
            onClick={() => {
              props.onSearch(
                SearchUtil.clearSearchParams(props.searchParams, {
                  preservedFilterKeys: [],
                  filtersToReset: getDefaultRequestV2DefinitionListingSearchParams().filters,
                })
              );
              props.onSelectSavedSearch(undefined);
            }}
          />,
          <CrumbLink key="root" text={props.activeSavedSearch.name} />,
        ]}
        settingsIcon={props.settingsComponents}
      />
    );
  }

  return (
    <SplitBreadcrumbs crumbs={[<span key="root">Requests</span>]} settingsIcon={props.settingsComponents} />
  );
}

export function getRequestV2DefinitionListingURL(
  prevProps: RequestV2DefinitionListingProps,
  props: RequestV2DefinitionListingProps
): NavigationUpdate | null {
  return props.isExpanded
    ? {
        url: getNavigationURL('/request-v2-template/listing', props.searchParams, props.filterErrors),
        options: {replace: prevProps.isExpanded === props.isExpanded},
      }
    : null;
}

export default withSearching(
  RequestV2DefinitionListing,
  SearchType.REQUEST_V2_TYPES,
  SECTIONS.REQUESTS_V2_DEFINITIONS,
  {
    defaultSearchParams: getDefaultRequestV2DefinitionListingSearchParams(),
    getNavigationUpdate: getRequestV2DefinitionListingURL,
  }
);
